import {priority} from '@priority-software/priority-proto-api/lib';
import {TreeNodeProps} from '@priority-software/priority-style-react';
import {DataInclusionTreeNode} from './types';
import {
    scaleMarks,
    ChartDimensions,
    SingleSeriesChartTypesOptions,
    MultiSeriesChartTypesOptions
} from './chartConsts';
import {Field} from '../../stores/Form/Field';
import {reportGeneratorsStore} from './stores/ReportGeneratorsStore';
import {gwtOperators, getOriginalCompanytitle} from './serialize';
import {IsCompany} from './consts';
import {HelpData} from './types';
import {startEntity} from '../../_shared/GWTBridge/invoke/genPage';
import texts from './texts';

class CustomSet {
    private items: Set<{val: string; origVal: string}>;
    constructor() {
        this.items = new Set();
    }

    add(obj) {
        if (![...this.items].some(item => item.val === obj.val && item.origVal === obj.origVal)) {
            this.items.add(obj);
        }
    }

    getValues() {
        return [...this.items];
    }
}

export const getColFormTitle = (
    col: priority.netitems.nrest.ITreeNode,
    subForms: priority.netitems.nrest.ITreeNode[] = [],
    forms: Map<string, priority.netitems.nrest.ITreeNode>
): string => {
    const nodeID = col.NodeId.split(':')[0];
    return forms.get(nodeID)?.Title || subForms.find(subForm => subForm.NodeId === nodeID)?.Title;
};

export const getMultiSeriesCategories = (data, index, isCompany, companies) => {
    const set = new CustomSet();
    data.map(row => set.add({val: row.Val.Val[index], origVal: row.Orig.Val[index]}));
    const category = set.getValues().map(item => {
        return {
            label: item.val,
            originalLabel: isCompany ? getOriginalCompanytitle(item.val, companies) : item.origVal
        };
    });
    return {category};
};

export const getSeriesNames = (data, index, isCompany, companies) => {
    const set = new CustomSet();
    data.map(row => set.add({val: row.Val.Val[index], origVal: row.Orig.Val[index]}));
    const seriesnames = set.getValues().map(item => {
        return {
            seriesname: item.val,
            origSerieName: isCompany ? getOriginalCompanytitle(item.val, companies) : item.origVal,
            data: []
        };
    });
    return seriesnames;
};

export const genScaleMarks = (fieldsMap: [number, Field][]) => {
    const fields = fieldsMap?.map(field => field[1]);
    let upperLimit = 0;

    const initialValues = scaleMarks.map(mark => {
        const currField = fields.find(field => field.columnId === mark.colId);
        const currValue = currField.currentValue?.toString().replace(/,/g, '') || '0';
        const maxValue = Number(currValue);
        if (maxValue > upperLimit) {
            upperLimit = maxValue;
        }
        return {
            code: mark.hexColor,
            maxValue
        };
    });

    const sorted = initialValues.sort((a, b) => {
        return a.maxValue - b.maxValue;
    });

    const color = sorted.map((mark, idx) => {
        if (idx === 0) {
            return {...mark, maxValue: mark.maxValue.toString(), minValue: '0'};
        } else {
            return {
                ...mark,
                maxValue: mark.maxValue.toString(),
                minValue: sorted[idx - 1].maxValue.toString()
            };
        }
    });

    const baseValueField = fields.find(field => field.columnId === 3);
    const lowerLimit = Number(baseValueField.currentValue?.toString().replace(/,/g, '') || '0');

    return {lowerLimit, upperLimit, colorRange: {color}};
};

export const getIsSpecialTitle = (type: SingleSeriesChartTypesOptions): boolean => {
    return [
        SingleSeriesChartTypesOptions.pie2d,
        SingleSeriesChartTypesOptions.doughnut2d,
        SingleSeriesChartTypesOptions.pyramid,
        SingleSeriesChartTypesOptions.funnel
    ].includes(type);
};
export const handleDeleteCompanyCol = () => {
    const {currentScreen} = reportGeneratorsStore;
    if (currentScreen.chartInfo?.xAxis?.[IsCompany]) {
        currentScreen.resetXAxisInfo();
    }
    if (currentScreen.chartInfo?.yAxis?.[IsCompany]) {
        currentScreen.resetYAxisInfo();
    }
    if (currentScreen.chartInfo?.bubbleConfig?.zBubbleSize?.[IsCompany]) {
        currentScreen.resetZAxisInfo();
    }
    if (currentScreen.chartInfo?.bubbleConfig?.category?.[IsCompany]) {
        currentScreen.resetBubbleCategory();
    }
    if (currentScreen.chartInfo?.series?.[IsCompany]) {
        currentScreen.resetSeriesDimension();
    }
};

export const toggleHiddenCol = (colId: number) => {
    const {currentScreen} = reportGeneratorsStore;
    const {
        chartInfo,
        resetXAxisInfo,
        resetYAxisInfo,
        resetZAxisInfo,
        resetBubbleCategory,
        resetSeriesDimension
    } = currentScreen;

    if (Number(chartInfo?.xAxis?.ColId) === colId) {
        resetXAxisInfo();
    }
    if (Number(chartInfo?.yAxis?.ColId) === colId) {
        resetYAxisInfo();
    }
    if (Number(chartInfo?.bubbleConfig?.zBubbleSize?.ColId) === colId) {
        resetZAxisInfo();
    }
    if (Number(chartInfo?.bubbleConfig?.category?.ColId) === colId) {
        resetBubbleCategory();
    }
    if (Number(chartInfo?.series?.ColId) === colId) {
        resetSeriesDimension();
    }
};

export const handleDeletCol = (colId: number) => {
    toggleHiddenCol(colId);
};

export const handleColTitleUpdate = (colId: number) => {
    const {currentScreen} = reportGeneratorsStore;
    const {
        chartInfo,
        resetXAxisInfo,
        resetYAxisInfo,
        resetZAxisInfo,
        resetBubbleCategory,
        resetSeriesDimension
    } = currentScreen;
    const {xAxis, yAxis, series, bubbleConfig} = chartInfo;
    if (Number(xAxis?.ColId) === colId) {
        resetXAxisInfo();
    } else if (Number(yAxis?.ColId) === colId) {
        resetYAxisInfo();
    } else if (Number(series?.ColId) === colId) {
        resetSeriesDimension();
    } else if (Number(bubbleConfig?.zBubbleSize?.ColId) === colId) {
        resetZAxisInfo();
    } else if (Number(bubbleConfig?.category?.ColId) === colId) {
        resetBubbleCategory();
    }
};

export const handleDateFormatUpdate = (colId: number, value: number) => {
    const {currentScreen} = reportGeneratorsStore;
    const {chartInfo} = currentScreen;
    const {xAxis, series, bubbleConfig} = chartInfo;
    if (Number(xAxis?.ColId) === colId) {
        xAxis.DateFormat = value;
    }
    if (Number(series?.ColId) === colId) {
        series.DateFormat = value;
    }
    if (Number(bubbleConfig?.category?.ColId) === colId) {
        bubbleConfig.category.DateFormat = value;
    }
};

export const handleNumericFormatUpdate = (colId: number, value: number) => {
    const {currentScreen} = reportGeneratorsStore;
    const {chartInfo} = currentScreen;
    const {xAxis, yAxis, bubbleConfig} = chartInfo;
    if (Number(yAxis?.ColId) === colId) {
        yAxis.NumericFormat = value;
    }
    if (Number(xAxis?.ColId) === colId) {
        if (chartInfo.type === SingleSeriesChartTypesOptions.bubble) {
            xAxis.NumericFormat = value;
        }
    }
    if (Number(bubbleConfig?.zBubbleSize?.ColId) === colId) {
        bubbleConfig.zBubbleSize.NumericFormat = value;
    }
};

export const handleGroupUpdate = (colId: number, value: number) => {
    const {currentScreen} = reportGeneratorsStore;
    const {chartInfo} = currentScreen;
    const {xAxis, series, bubbleConfig} = chartInfo;
    if (Number(xAxis?.ColId) === colId) {
        xAxis.Group = value;
    }
    if (Number(series?.ColId) === colId) {
        series.Group = value;
    }
    if (Number(bubbleConfig?.category?.ColId) === colId) {
        bubbleConfig.category.Group = value;
    }
};

export const handleFieldsUpdate = (columns: priority.netitems.nrest.IReportCol[]) => {
    const {currentScreen} = reportGeneratorsStore;
    const {
        chartInfo,
        resetXAxisInfo,
        resetYAxisInfo,
        resetZAxisInfo,
        resetBubbleCategory,
        resetSeriesDimension,
        resetChartDimensions
    } = currentScreen;
    if (!columns) {
        resetChartDimensions();
        return;
    }
    const {xAxis, yAxis, series, bubbleConfig} = chartInfo;
    if (xAxis && !xAxis.IsExpression) {
        const col = columns.find(col => col.ColId === Number(xAxis.ColId));
        if (!col) {
            resetXAxisInfo();
        }
    }
    if (yAxis && !yAxis.IsExpression) {
        const col = columns.find(col => col.ColId === Number(yAxis.ColId));
        if (!col) {
            resetYAxisInfo();
        }
    }
    if (series && !series.IsExpression) {
        const col = columns.find(col => col.ColId === Number(series.ColId));
        if (!col) {
            resetSeriesDimension();
        }
    }
    if (bubbleConfig) {
        const {zBubbleSize, category} = bubbleConfig;
        if (zBubbleSize && !zBubbleSize.IsExpression) {
            const col = columns.find(col => col.ColId === Number(zBubbleSize.ColId));
            if (!col) {
                resetZAxisInfo();
            }
        }
        if (category && !category.IsExpression) {
            const col = columns.find(col => col.ColId === Number(category.ColId));
            if (!col) {
                resetBubbleCategory();
            }
        }
    }
};

export const prepareExpressionsToStore = (columns: priority.netitems.nrest.IReportCol[]) => {
    const regex = /#\w+/g;
    const newColumns = columns.map(column => {
        if (column.Expr) {
            let expression = column.Expr;
            const idsArray = column.Expr?.match(regex);
            idsArray?.forEach(id => {
                const column = columns.find(
                    column => column.ColId?.toString() === id.split('#')[1]
                );
                const columnName = column?.NodeId?.split(':')[1] || column?.Title;
                expression = expression.replace(id, `<<${columnName}>>`);
            });

            return {...column, Expr: expression};
        }
        return column;
    });

    return newColumns;
};

export const prepareExpressionsToServer = (columns: priority.netitems.nrest.IReportCol[]) => {
    const regex = /<<[^<>]+>>/g;
    const regexCutSymbols = /<<|>>/g;
    const serverColumns = columns.map(column => {
        if (column.Expr) {
            let expression = column.Expr;
            const titlesArray = column.Expr?.match(regex)?.map(title =>
                title.replace(regexCutSymbols, '')
            );
            titlesArray?.forEach(title => {
                const columnId = columns.find(
                    column => column.NodeId?.split(':')[1] === title || column.Title === title
                )?.ColId;

                expression = expression.replace(`<<${title}>>`, `#${columnId}`);
            });

            return {...column, Expr: expression};
        }
        return column;
    });

    return serverColumns;
};

export const handleAxesChange = (axis: string, type: string) => {
    const {currentScreen} = reportGeneratorsStore;
    const {resetExplantoryReport} = currentScreen;
    switch (type) {
        // Multi-Series
        case MultiSeriesChartTypesOptions.msline:
        case MultiSeriesChartTypesOptions.mscolumn2d:
        case MultiSeriesChartTypesOptions.msbar2d:
        case MultiSeriesChartTypesOptions.stackedcolumn2d:
        case MultiSeriesChartTypesOptions.stackedbar2d:
            if (axis === ChartDimensions.xAxis || axis === ChartDimensions.series) {
                resetExplantoryReport();
            }
            break;
        // Basic Single-Series
        case SingleSeriesChartTypesOptions.column2d:
        case SingleSeriesChartTypesOptions.bar2d:
        case SingleSeriesChartTypesOptions.line:
        case SingleSeriesChartTypesOptions.pie2d:
        case SingleSeriesChartTypesOptions.doughnut2d:
        case SingleSeriesChartTypesOptions.pyramid:
        case SingleSeriesChartTypesOptions.funnel:
            if (axis === ChartDimensions.xAxis) {
                resetExplantoryReport();
            }
            break;
        // Bubble Chart
        case SingleSeriesChartTypesOptions.bubble:
            if (axis === ChartDimensions.category) {
                resetExplantoryReport();
            }
            break;
    }
};

export const isTwoInputsOperator = (operator: string): boolean => {
    return [gwtOperators.Between, gwtOperators.AndNot, gwtOperators.Or].includes(operator);
};

const findNextExpressionId = (expressionIds: Array<number>) => {
    let current = 900;
    while (expressionIds.includes(current)) {
        current++;
    }
    return current;
};

export const getExpressionId = () => {
    const {currentScreen} = reportGeneratorsStore;
    const {currentReport} = currentScreen;
    const {getExpressionIds, addExpressionId} = currentReport;
    const expressionIds = getExpressionIds();
    let nextExpId;
    if (expressionIds.length <= 0) {
        nextExpId = 900;
    } else {
        nextExpId = findNextExpressionId(expressionIds);
    }
    addExpressionId(nextExpId);
    return nextExpId;
};

export const getHelpData = content => {
    try {
        const parsedContent = JSON.parse(content);
        const helpData: HelpData = parsedContent[0];
        const linkList = helpData.HyperLinks.map(link => {
            return {...link, linkData: {ename: link.ename}};
        });
        return {
            content: helpData.text,
            linkList,
            onLinkClick: data => startEntity(data.ename, null, '*')
        };
    } catch (err) {
        return {content};
    }
};

export const getChartLabelByType = (
    type: SingleSeriesChartTypesOptions | MultiSeriesChartTypesOptions
) => {
    switch (type) {
        case SingleSeriesChartTypesOptions.column2d:
            return texts.columnChart;
        case SingleSeriesChartTypesOptions.bar2d:
            return texts.barChart;
        case SingleSeriesChartTypesOptions.line:
            return texts.lineChart;
        case SingleSeriesChartTypesOptions.pie2d:
            return texts.pieChart;
        case SingleSeriesChartTypesOptions.doughnut2d:
            return texts.doughnutChart;
        case SingleSeriesChartTypesOptions.bubble:
            return texts.bubbleChart;
        case SingleSeriesChartTypesOptions.angulargauge:
            return texts.gaugeChart;
        case SingleSeriesChartTypesOptions.pyramid:
            return texts.pyramidChart;
        case SingleSeriesChartTypesOptions.funnel:
            return texts.funnelChart;
        case SingleSeriesChartTypesOptions.kpi:
            return texts.kpiChart;
        case MultiSeriesChartTypesOptions.mscolumn2d:
            return texts.msColumnsChart;
        case MultiSeriesChartTypesOptions.msbar2d:
            return texts.msBarChart;
        case MultiSeriesChartTypesOptions.msline:
            return texts.msLineChart;
        case MultiSeriesChartTypesOptions.stackedcolumn2d:
            return texts.stackedColumnChart;
        case MultiSeriesChartTypesOptions.stackedbar2d:
            return texts.stackedBarChart;
    }
};

export const getChartTooltipByType = (
    type: SingleSeriesChartTypesOptions | MultiSeriesChartTypesOptions
) => {
    switch (type) {
        case SingleSeriesChartTypesOptions.column2d:
        case SingleSeriesChartTypesOptions.bar2d:
            return texts.barAndColTooltip;
        case SingleSeriesChartTypesOptions.line:
            return texts.lineTooltip;
        case SingleSeriesChartTypesOptions.pie2d:
        case SingleSeriesChartTypesOptions.doughnut2d:
            return texts.pieAndDoughnutTooltip;
        case SingleSeriesChartTypesOptions.bubble:
            return texts.bubbleTooltip;
        case SingleSeriesChartTypesOptions.angulargauge:
            return texts.gaugeTooltip;
        case SingleSeriesChartTypesOptions.pyramid:
            return texts.pyramidTooltip;
        case SingleSeriesChartTypesOptions.funnel:
            return texts.funnelTooltip;
        case SingleSeriesChartTypesOptions.kpi:
            return texts.kpiTooltip;
        case MultiSeriesChartTypesOptions.mscolumn2d:
        case MultiSeriesChartTypesOptions.msbar2d:
            return texts.multiColAndBarTooltip;
        case MultiSeriesChartTypesOptions.msline:
            return texts.multiLineTooltip;
        case MultiSeriesChartTypesOptions.stackedcolumn2d:
        case MultiSeriesChartTypesOptions.stackedbar2d:
            return texts.multiStackedColumnTooltip;
    }
};

export const getXAxisPlaceHolderByType = (
    type: SingleSeriesChartTypesOptions | MultiSeriesChartTypesOptions
) => {
    switch (type) {
        case SingleSeriesChartTypesOptions.column2d:
        case SingleSeriesChartTypesOptions.bar2d:
        case SingleSeriesChartTypesOptions.line:
            return texts.commonXAxisPlaceHoder;
        case SingleSeriesChartTypesOptions.pie2d:
            return texts.pieCategoryPlaceHolder;
        case SingleSeriesChartTypesOptions.pyramid:
            return texts.pyramidCategoryPlaceHolder;
        case SingleSeriesChartTypesOptions.doughnut2d:
            return texts.doughnutCategoryPlaceHolder;
        case SingleSeriesChartTypesOptions.funnel:
            return texts.funnelCategoryPlaceHolder;
        case SingleSeriesChartTypesOptions.bubble:
            return texts.bubbleXAxisPlaceHoder;
        case MultiSeriesChartTypesOptions.mscolumn2d:
        case MultiSeriesChartTypesOptions.msbar2d:
        case MultiSeriesChartTypesOptions.msline:
        case MultiSeriesChartTypesOptions.stackedcolumn2d:
        case MultiSeriesChartTypesOptions.stackedbar2d:
            return texts.msPrimaryXAxisPlaceHolder;
    }
};

export const getYAxisPlaceHolderByType = (
    type: SingleSeriesChartTypesOptions | MultiSeriesChartTypesOptions
) => {
    switch (type) {
        case SingleSeriesChartTypesOptions.column2d:
        case SingleSeriesChartTypesOptions.bar2d:
        case SingleSeriesChartTypesOptions.line:
            return texts.commonYAxisPlaceHoder;
        case SingleSeriesChartTypesOptions.pie2d:
        case SingleSeriesChartTypesOptions.pyramid:
        case SingleSeriesChartTypesOptions.doughnut2d:
        case SingleSeriesChartTypesOptions.funnel:
            return texts.valuesPlaceHolder;
        case SingleSeriesChartTypesOptions.bubble:
            return texts.bubbleYAxisPlaceHoder;
        case SingleSeriesChartTypesOptions.angulargauge:
            return texts.gaugeMeasuredValuePlaceHolder;
        case SingleSeriesChartTypesOptions.kpi:
            return texts.cardValuePlaceHolder;
        case MultiSeriesChartTypesOptions.mscolumn2d:
        case MultiSeriesChartTypesOptions.msbar2d:
        case MultiSeriesChartTypesOptions.msline:
        case MultiSeriesChartTypesOptions.stackedcolumn2d:
        case MultiSeriesChartTypesOptions.stackedbar2d:
            return texts.msYAxisPlaceHolder;
    }
};

export const getFormToUpdate = (map, targetId, isZoomedFromSubform = false) => {
    for (const item of map) {
        if (item.id === targetId) {
            return item;
        }

        if (item.nodes?.length > 0) {
            if (isZoomedFromSubform) {
                if (item.nodes.findIndex(node => node.id === targetId) >= 0) {
                    return item;
                } else {
                    const result = getFormToUpdate(item.nodes, targetId, true);
                    if (result) {
                        return result;
                    }
                }
            } else {
                const result = getFormToUpdate(item.nodes, targetId);
                if (result) {
                    return result;
                }
            }
        }
    }
    return null;
};

export const updateTree = (nodes: DataInclusionTreeNode[], selectedId: string): TreeNodeProps[] => {
    const handleUpdate = (
        nodes: DataInclusionTreeNode[],
        selectedId: string
    ): [TreeNodeProps[], boolean] => {
        let foundSelected = false;

        const updatedNodes = nodes?.map(node => {
            const isNodeSelected = node.id === selectedId;
            let [updatedChildren, childHasSelected] = handleUpdate(node.nodes, selectedId);

            // If this node is selected or one of its children contains the selected node
            if (isNodeSelected || childHasSelected) {
                foundSelected = true;

                // If the node is the selected node, disable all its children
                if (isNodeSelected) {
                    updatedChildren = markDisabledNodes(node.nodes);
                }

                return {
                    ...node,
                    isSelected: isNodeSelected,
                    isExpanded: true,
                    isDisabled: !isNodeSelected,
                    nodes: updatedChildren
                };
            }

            // Reset this node if it's not in the selected path
            return {
                ...node,
                isSelected: false,
                isExpanded: false,
                isDisabled: true,
                nodes: updatedChildren
            };
        });

        return [updatedNodes, foundSelected];
    };

    // Recursive function to mark all subsequent nodes as disabled
    const markDisabledNodes = (nodes: TreeNodeProps[]): TreeNodeProps[] => {
        return nodes?.map(node => ({
            ...node,
            isSelected: true,
            isDisabled: true,
            nodes: markDisabledNodes(node.nodes)
        }));
    };

    const [newNodes] = handleUpdate(nodes, selectedId);
    return newNodes;
};

export const clearOuterJoinSelection = (nodes: DataInclusionTreeNode[]): TreeNodeProps[] => {
    const clearedTree = nodes.map(node => {
        const updatedNode = {
            ...node,
            isDisabled: false,
            isSelected: false,
            nodes: node.nodes ? clearOuterJoinSelection(node.nodes) : []
        };

        return updatedNode;
    });
    return clearedTree;
};

export const getSelectedParentNodeIds = (
    selected: priority.netitems.nrest.ITreeNode[]
): string[] => {
    const selectedIds = new Set<string>();
    selected.forEach(node => {
        const parentNodeId = node.NodeId.split(':')[0];
        selectedIds.add(parentNodeId);
    });
    return Array.from(selectedIds);
};

const markVisibleNodes = (nodes: TreeNodeProps[], parentNodeIds: string[]) => {
    const markVisibility = node => {
        const hasColumnInReport = parentNodeIds.includes(node.id);

        // Recursively process child nodes
        const updatedChildren = (node.nodes || []).map(markVisibility);

        // Determine if the node has visible children
        const hasVisibleChildren = updatedChildren.some(child => child.isVisible);

        // Apply the visibility rules:
        // Main nodes should be visible only if they have visible children
        // Children should be visible any of their columns are part of the report
        // - OR if it has visible children (standard rule)
        const isVisible =
            node.isMain || node.extraData?.isZoomed
                ? updatedChildren.length > 0 && hasVisibleChildren
                : hasColumnInReport || hasVisibleChildren;
        return {
            ...node,
            isVisible,
            nodes: updatedChildren
        };
    };

    // Process the entire tree
    return nodes.map(markVisibility);
};

const filterVisibleNodes = (nodes: any[]) => {
    return nodes
        .map(node => {
            // Recursively filter the children
            const filteredChildren = filterVisibleNodes(node.nodes || []);

            // Include the node only if it is visible or has visible children
            if (node.isVisible || filteredChildren.length > 0) {
                return {
                    ...node,
                    nodes: filteredChildren
                };
            }

            // Exclude nodes that don't satisfy the conditions
            return null;
        })
        .filter(Boolean);
};

const getIsExpandedNode = (nodes: TreeNodeProps[]) => {
    for (const node of nodes) {
        if (node.isExpanded) {
            return true;
        }
        if (node.nodes && getIsExpandedNode(node.nodes)) {
            return true;
        }
    }
    return false;
};

export const getNodesToProcess = (nodes: TreeNodeProps[], outerJoinId) => {
    if (!nodes) {
        return null;
    } else if (outerJoinId > 0) {
        return updateTree(nodes, outerJoinId.toString());
    } else if (outerJoinId === 0) {
        const clearedTree = clearOuterJoinSelection(nodes);
        const hasExpandedNode = getIsExpandedNode(clearedTree);
        if (!hasExpandedNode) {
            clearedTree[0].isExpanded = true;
        }
        return clearedTree;
    } else {
        nodes[0].isExpanded = true;
        return nodes;
    }
};

export const getNodesToRender = (selectedCols, nodesToProcess) => {
    const parentNodeIds = getSelectedParentNodeIds(selectedCols);
    const markedNodes = markVisibleNodes(nodesToProcess, parentNodeIds);
    const visibleNodes = filterVisibleNodes(markedNodes);
    return visibleNodes;
};
